import { ActionSheet, ActionSheetButtonStyle } from '@capacitor/action-sheet';
import { Modal } from '@mgh-app/component-library';
import { GhostButton } from '@mgh-app/component-library';
import { Button, Spinner, TextField } from '@mgh-app/component-library';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { useAppNavigation } from 'navigation/useAppNavigation';
import { useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import {
  SupportItem,
  useCreateSupportTicketMutation,
  useGetSupportListQuery,
} from 'services/AccountService';
import { appVersion } from 'services/apiConfig';
import { useGetUserDetailsQuery } from 'services/UsersService';
import {
  borderColor,
  borderStyle,
  borderWidth,
  display,
  gridTemplateRows,
  lineHeight,
  margin,
  overflow,
  textColor,
  twCls,
  width,
} from 'style';

export const showCreateTicketActions = async (
  supportItems: SupportItem[],
  onSelectItem: (index: number) => void,
) => {
  const result = await ActionSheet.showActions({
    title: 'Message MyGlobalHome',
    options: [
      ...supportItems.map((item) => ({ title: item.name })),
      {
        title: 'Cancel',
        style: ActionSheetButtonStyle.Destructive,
      },
    ],
  });

  onSelectItem(result.index);
};

interface Fields {
  message?: string;
}

export const SupportTicket = (): JSX.Element => {
  const { id } = useParams();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const { handleSubmit, control } = useForm<Fields>();
  const { navigateHome } = useAppNavigation();
  const { data: supportItems, isLoading } = useGetSupportListQuery();
  const [createSupportTicket] = useCreateSupportTicketMutation();
  const { data: userDetails } = useGetUserDetailsQuery();
  const currentSupportItem =
    supportItems && supportItems.find((item) => item.id.toFixed().toString() === id);

  const onSubmit: SubmitHandler<Fields> = (data) => {
    createSupportTicket({
      subject: currentSupportItem?.name,
      description: data.message,
      email: userDetails?.email,
      globalHomeId: currentSupportItem?.id.toFixed().toString(),
      unitId: userDetails?.unitId,
      appVersion: appVersion,
    });
    setShowConfirmation(true);
  };

  const onDismiss = () => {
    setShowConfirmation(false);
    navigateHome(true);
  };

  return (
    <form
      className={twCls(display('grid'), gridTemplateRows('grid-rows-min-fc-min'))}
      onSubmit={handleSubmit(onSubmit)}
    >
      {isLoading && <Spinner />}
      {!isLoading && (
        <>
          <p
            className={twCls(
              textColor('text-grey-800'),
              margin('mt-12', 'mb-3'),
              borderColor('border-grey-800'),
              borderWidth('border-t', 'border-b'),
              borderStyle('border-solid'),
              lineHeight('leading-9'),
            )}
          >
            Subject: {currentSupportItem?.name}
          </p>
          <div className={twCls(overflow('overflow-auto'))}>
            <Controller
              name="message"
              control={control}
              rules={{
                required: true,
              }}
              render={({ field }) => (
                <TextField
                  className={twCls(width('w-full'))}
                  multiline
                  placeholder="Please type here leaving as much detail as possible and we'll get back to you ASAP"
                  InputProps={{
                    disableUnderline: true,
                  }}
                  {...field}
                />
              )}
            />
          </div>
          <Button className={twCls(margin('mt-4'))} onClick={handleSubmit(onSubmit)}>
            Send
          </Button>
          <Modal open={showConfirmation}>
            <DialogTitle>MyGlobalHome</DialogTitle>
            <DialogContent>Support ticket created successfully</DialogContent>
            <DialogActions>
              <GhostButton onClick={onDismiss}>Dismiss</GhostButton>
            </DialogActions>
          </Modal>
        </>
      )}
    </form>
  );
};
